/**
 * Private Provider documents (spec 019 D15): insurance and background check
 * files go to `service-docs/<userId>/` through `getPresignedUrlForServiceDocument`,
 * then their key is sent with the submit mutation. Never shown on a public URL.
 */

export const SERVICE_DOCUMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const;
export const SERVICE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Why a file cannot be sent, or null. Same limits as the API. */
export function documentFileProblem(file: Pick<File, 'type' | 'size'> | null | undefined) {
  if (!file) return 'Choose a file';
  if (!(SERVICE_DOCUMENT_TYPES as readonly string[]).includes(file.type)) {
    return 'Upload a PDF, JPEG, PNG, WebP or HEIC file';
  }
  if (file.size <= 0) return 'The file is empty';
  if (file.size > SERVICE_DOCUMENT_MAX_BYTES) return 'The file is larger than 10 MB';
  return null;
}

type Presign = (
  contentType: string,
  contentLength: number,
) => Promise<{ url: string; key: string }>;

/** Uploads one document and returns its key for the submit mutation. */
export async function uploadServiceDocument(file: File, presign: Presign): Promise<string> {
  const problem = documentFileProblem(file);
  if (problem) throw new Error(problem);
  const { url, key } = await presign(file.type, file.size);
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return key;
}
